import { zodResolver } from '@hookform/resolvers/zod';
import { FormLabel } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { Info } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import PhoneInput from 'react-phone-input-2';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import apiClient from '../../../api/client';
import PhoneVerificationModal from '../../../components/phone-verification-modal';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot
} from '../../../components/ui/input-otp';
import { FaceVerifyScreens } from '../../../constants/face.const';
import { PhoneUpdateFlowStep } from '../../../constants/settings.const';
import FailedSVG from '../../register/FailedScreen/components/FailedSvg';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../../components/ui/tooltips';

const formSchema = z.object({
  oldPhoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .max(15, 'Phone number must be at most 15 digits long'),
  newPhoneNumber: z
    .string()
    .min(1, 'Phone number is required')
    .max(15, 'Phone number must be at most 15 digits long'),
  pin: z.string().length(6, 'PIN must be exactly 6 digits long')
});

type FormSchema = z.infer<typeof formSchema>;

type PhoneValidateData = {
  phoneNumber: string;
  countryCode: string;
  type?: 'existing' | 'new';
};

type PhoneVerifyData = {
  phoneNumber: string;
  countryCode: string;
  otp: string;
};

interface IProps {
  lastUpdated: string;
}

interface VerifyFaceResponse {
  success: boolean;
  status: number;
  data: {
    VerificationUrl: string;
    BizToken: string;
    VerificationURL: string;
    RequestId: string;
  };
}

const UpdatePhoneNumber: React.FC<IProps> = ({ lastUpdated }) => {
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      oldPhoneNumber: '60',
      newPhoneNumber: '60'
    }
  });
  const [searchParams, setSearchParams] = useSearchParams();

  const currentStep =
    (Number(searchParams.get('step')) as PhoneUpdateFlowStep) ||
    PhoneUpdateFlowStep.Warning;

  const sdkToken = searchParams.get('token');

  const {
    register,
    getValues,
    setValue,
    watch,
    setError,
    formState: { errors },
    clearErrors
  } = form;

  const onSubmit = (data: FormSchema) => {
    console.log('Form Data:', data);
  };

  const [openDialog, setOpenDialog] = useState(false);
  const [showFaceVerificationError, setShowFaceVerificationError] =
    useState(false);

  const phoneValidate = useMutation({
    mutationFn: async (data: PhoneValidateData) => {
      // Simulate an API call to update the phone number
      await apiClient.post('/users/phone/validate', data);
      return data; // Return data so it's available in onSuccess
    },
    onSuccess: (data) => {
      console.log('OTP sent successfully');

      const phoneNumber =
        data.type === 'existing'
          ? getValues('oldPhoneNumber')
          : getValues('newPhoneNumber');
      const phoneUtil = PhoneNumberUtil.getInstance();
      const number = phoneUtil.parseAndKeepRawInput(
        phoneNumber?.startsWith('+') ? phoneNumber : `+${phoneNumber}`
      );
      if (number.getNationalNumber() && number.getCountryCode()) {
        setVerifyPhone(
          `${number.getCountryCode()}-${number.getNationalNumber()}`
        );
      }
    },
    onError: (error, _variables) => {
      console.error('Error updating phone number:', error);

      setError(
        _variables.type === 'existing' ? 'oldPhoneNumber' : 'newPhoneNumber',
        {
          type: 'manual',
          message:
            (error as any)?.response?.data?.error?.message ||
            'Invalid phone number'
        }
      );
    }
  });

  // useEffect(() => {
  //   if (cantAccessOldPhone) {
  //     setOpenDialog(true);
  //     const params = new URLSearchParams(window.location.search);
  //     params.set('step', PhoneUpdateFlowStep.EnterNewPhone.toString());
  //     params.delete('cantAccessOldPhone');
  //     setSearchParams(params);
  //   }
  // }, [cantAccessOldPhone, setSearchParams]);

  // Handle face verification return
  const isVerified = searchParams.get('isVerified') === 'true';
  const isVerificationFailed = searchParams.get('isVerified') === 'false';
  const flowType = searchParams.get('flow') as FaceVerifyScreens;

  useEffect(() => {
    if (
      isVerified &&
      [
        FaceVerifyScreens.UPDATE_PHONE,
        FaceVerifyScreens.UPDATE_PHONE_2
      ].includes(flowType)
    ) {
      setOpenDialog(true);
      const params = new URLSearchParams(window.location.search);

      // Check if step is already set (from settings.tsx)
      const stepParam = params.get('step');
      if (!stepParam) {
        params.set('step', PhoneUpdateFlowStep.EnterNewPhone.toString());
      }

      params.delete('isVerified');
      params.delete('flow');
      setSearchParams(params);
    } else if (
      isVerificationFailed &&
      [
        FaceVerifyScreens.UPDATE_PHONE,
        FaceVerifyScreens.UPDATE_PHONE_2
      ].includes(flowType)
    ) {
      // Face verification failed, show error dialog and stop the flow
      setShowFaceVerificationError(true);
      const params = new URLSearchParams(window.location.search);
      params.delete('isVerified');
      params.delete('flow');
      setSearchParams(params);
    }
  }, [isVerified, isVerificationFailed, flowType, setSearchParams]);

  const handleContinue = (data: FormSchema) => {
    clearErrors('oldPhoneNumber');
    const phoneUtil = PhoneNumberUtil.getInstance();
    try {
      const formattedValue = data.oldPhoneNumber.startsWith('+')
        ? data.oldPhoneNumber
        : `+${data.oldPhoneNumber}`;
      const number = phoneUtil.parseAndKeepRawInput(formattedValue);

      if (!phoneUtil.isValidNumber(number)) {
        throw new Error('Invalid phone number');
      }

      if (!number.getNationalNumber() || !number.getCountryCode()) {
        throw new Error('Phone number is incomplete');
      }

      phoneValidate.mutate({
        phoneNumber: number.getNationalNumber()?.toString() || '',
        countryCode: number.getCountryCode()?.toString() || '',
        type: 'existing'
      });
    } catch (error) {
      console.error('Error parsing phone number:', error);
      setError('oldPhoneNumber', {
        type: 'manual',
        message: (error as any)?.message || 'Invalid phone number'
      });
    }
  };

  const verifyOtpForCurrentPhone = useMutation({
    mutationFn: async (data: { otp: string }) => {
      await apiClient.post('/users/verify/otp', data);
      return data;
    },
    onSuccess: (data) => {
      console.log('Phone number validated successfully');
      const params = new URLSearchParams(window.location.search);
      params.set('step', PhoneUpdateFlowStep.EnterPin.toString());
      setSearchParams(params);
      setVerifyPhone('');
    },
    onError: (error) => {
      console.error('Error validating phone number:', error);
      if (
        (error as any)?.response?.data?.error?.code === 'OTP_FAILED_ATTEMPTS'
      ) {
        return;
      }
      setError('oldPhoneNumber', {
        type: 'manual',
        message:
          (error as any)?.response?.data?.error?.message ||
          'Invalid phone number'
      });
      toast.error(
        (error as any)?.response?.data?.error?.message || 'Invalid phone number'
      );
    }
  });

  const handleVerifyOtpForCurrentPhone = (otp: string) => {
    verifyOtpForCurrentPhone.mutate({
      otp
    });
  };

  const getVerifyFaceToken = useMutation<
    VerifyFaceResponse,
    Error,
    FaceVerifyScreens
  >({
    mutationFn: (screen = FaceVerifyScreens.UPDATE_PHONE) =>
      apiClient
        .post<VerifyFaceResponse>('/face/register', { screen })
        .then((res) => res.data),
    onSuccess: (response) => {
      const url = response.data.VerificationUrl;
      window.location.href = url;
    },
    onError: (error) => {
      console.error('Face verify error', error);
    }
  });

  const validatePin = useMutation({
    mutationFn: async (pin: string) => {
      // Simulate an API call to validate the PIN
      await apiClient.post('/users/validate-pin', { pin });
      return pin;
    },
    onSuccess: () => {
      // * Can enable for testing - It will skip face scan
      // const params = new URLSearchParams(window.location.search);
      // params.set('step', PhoneUpdateFlowStep.EnterNewPhone.toString());
      // setSearchParams(params);
      // return;

      getVerifyFaceToken.mutate(FaceVerifyScreens.UPDATE_PHONE_2);
    },
    onError: (error) => {
      console.error('Error validating PIN:', error);
      setValue('pin', '');
      setError('pin', {
        type: 'manual',
        message: 'Invalid PIN. Please try again.'
      });
    }
  });

  const handleCheckPin = (pin: string) => {
    if (pin.length < 6) {
      return;
    }

    validatePin.mutate(pin);

    // const params = new URLSearchParams(window.location.search);
    // params.set('step', PhoneUpdateFlowStep.EnterNewPhone.toString());
    // setSearchParams(params);
  };

  const handleContinueNewPhone = (data: FormSchema) => {
    clearErrors('newPhoneNumber');
    const phoneUtil = PhoneNumberUtil.getInstance();
    try {
      const number = phoneUtil.parseAndKeepRawInput(
        data.newPhoneNumber?.startsWith('+')
          ? data.newPhoneNumber
          : `+${data.newPhoneNumber}`
      );

      if (!phoneUtil.isValidNumber(number)) {
        throw new Error('Invalid phone number');
      }

      phoneValidate.mutate({
        phoneNumber: number.getNationalNumber()?.toString() || '',
        countryCode: number.getCountryCode()?.toString() || '',
        type: 'new'
      });
    } catch (error) {
      setError('newPhoneNumber', {
        type: 'manual',
        message:
          (error as any)?.response?.data?.error?.message ||
          'Invalid phone number'
      });
      console.error('Error parsing phone number:', error);
    }
  };

  const confirmChangePhone = useMutation({
    mutationFn: async (data: PhoneVerifyData) => {
      // Simulate an API call to confirm the phone number change
      const phoneUtil = PhoneNumberUtil.getInstance();

      const number = phoneUtil.parseAndKeepRawInput(
        data.phoneNumber?.startsWith('+')
          ? data.phoneNumber
          : `+${data.phoneNumber}`
      );

      if (!phoneUtil.isValidNumber(number)) {
        throw new Error('Invalid phone number');
      }

      await apiClient.put('/users/me/phone', {
        phoneNumber: number.getNationalNumber()?.toString() || '',
        countryCode: number.getCountryCode()?.toString() || '',
        otp: data.otp
      });
      return data; // Return data so it's available in onSuccess
    },
    onSuccess: () => {
      setVerifyPhone('');
      console.log('Phone number updated successfully');
      const params = new URLSearchParams(window.location.search);
      params.set('step', PhoneUpdateFlowStep.Done.toString());
      setSearchParams(params);
    },
    onError: (error) => {
      console.error('Error confirming phone number:', error);
      setError('newPhoneNumber', {
        type: 'manual',
        message:
          (error as any)?.response?.data?.error?.message ||
          'Invalid phone number'
      });
      toast.error(
        (error as any)?.response?.data?.error?.message || 'Invalid phone number'
      );
    }
  });

  const handleVerifyOtpForNewPhone = (otp: string) => {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const number = phoneUtil.parseAndKeepRawInput(
      getValues('newPhoneNumber'),
      'MY'
    );

    confirmChangePhone.mutate({
      phoneNumber: number.getNationalNumber()?.toString() || '',
      countryCode: number.getCountryCode()?.toString() || '',
      otp
    });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    const params = new URLSearchParams(window.location.search);
    params.delete('step');
    setSearchParams(params);
    form.reset();
  };

  const [verifyPhone, setVerifyPhone] = useState('');

  const titleMapping = {
    [PhoneUpdateFlowStep.Warning]: 'Important!',
    [PhoneUpdateFlowStep.EnterCurrentPhone]: 'Enter Old Phone Number',
    [PhoneUpdateFlowStep.FaceScan]: 'Face Scan',
    [PhoneUpdateFlowStep.EnterPin]: 'Enter PIN To Continue',
    [PhoneUpdateFlowStep.EnterNewPhone]: 'Enter New Phone Number'
  };

  const contentMapping = {
    [PhoneUpdateFlowStep.Warning]: (
      <>
        <div className="space-y-4 text-sm text-gray-700">
          <p>
            Updating your phone number will remove all your currently linked
            payment methods for security reasons.
          </p>
          <p>You will need to re-add your payment details after the update.</p>
          <p>Are you sure you want to continue?</p>
        </div>

        <div className="mt-6 flex gap-3">
          <Button
            variant="outline"
            className="flex-1 rounded-full"
            onClick={handleCloseDialog}
            type="button"
          >
            Cancel
          </Button>
          <Button
            className="flex-1 rounded-full bg-[#5CB891] hover:bg-[#5CB891]"
            onClick={() => {
              const params = new URLSearchParams(window.location.search);
              params.set(
                'step',
                PhoneUpdateFlowStep.EnterCurrentPhone.toString()
              );
              setSearchParams(params);
            }}
            type="button"
          >
            Yes, Update Number
          </Button>
        </div>
      </>
    ),
    [PhoneUpdateFlowStep.EnterCurrentPhone]: (
      <>
        <FormLabel className="mb-2">Phone Number</FormLabel>
        <Controller
          name="oldPhoneNumber"
          control={form.control}
          render={({ field }) => (
            <PhoneInput
              country={'my'}
              value={field.value}
              onChange={(value: string) => {
                field.onChange(value);
                setValue('oldPhoneNumber', value);
              }}
              inputStyle={{ width: '100%' }}
              enableSearch={false}
              disableCountryCode={true}
              countryCodeEditable={true} // Allow users to edit the country code
              placeholder="Enter your phone number"
              autoFormat={false}
            />
          )}
        />
        {errors.oldPhoneNumber && (
          <p className="mt-10 text-center text-sm text-red-500">
            {errors.oldPhoneNumber.message}
          </p>
        )}

        <Button
          className="mt-5 rounded-full bg-[#5CB891] hover:bg-[#5CB891]"
          onClick={() => handleContinue(watch())}
          type="button"
        >
          Continue
        </Button>
      </>
    ),
    [PhoneUpdateFlowStep.FaceScan]: (
      <div>
        <p>Face scan verification.</p>
        {/* Add face scan component here */}
      </div>
    ),
    [PhoneUpdateFlowStep.EnterPin]: (
      <>
        <div className="flex flex-col items-center">
          <p className="mb-8 mt-2 text-center text-sm">
            Please enter your 6-digit PIN
          </p>
          <Controller
            name="pin"
            control={form.control}
            render={({ field }) => (
              <InputOTP
                {...field}
                maxLength={6}
                value={field.value}
                onChange={(e) => {
                  field.onChange(e);
                  handleCheckPin(e);
                }}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                </InputOTPGroup>
                <InputOTPGroup>
                  <InputOTPSlot index={1} />
                </InputOTPGroup>
                <InputOTPGroup>
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                </InputOTPGroup>
                <InputOTPGroup>
                  <InputOTPSlot index={4} />
                </InputOTPGroup>
                <InputOTPGroup>
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            )}
          />
          {errors.pin && (
            <p className="mt-2 text-sm text-red-500">{errors.pin.message}</p>
          )}
        </div>

        <div className="mx-auto">
          <Button variant={'link'} className="mt-4 text-[#5B5959]">
            Forget PIN?
          </Button>
        </div>
      </>
    ),
    [PhoneUpdateFlowStep.EnterNewPhone]: (
      <>
        <FormLabel className="mb-2">Phone Number</FormLabel>
        <Controller
          name="newPhoneNumber"
          control={form.control}
          render={({ field }) => (
            <PhoneInput
              country={'my'}
              value={field.value}
              onChange={(value: string) => {
                field.onChange(value);
                setValue('newPhoneNumber', value);
              }}
              inputStyle={{ width: '100%' }}
              enableSearch={false}
              disableCountryCode={true}
              countryCodeEditable={true} // Allow users to edit the country code
              placeholder="Enter your phone number"
              autoFormat={false}
            />
          )}
        />
        {errors.newPhoneNumber && (
          <p className="mt-10 text-center text-xs text-red-500">
            {errors.newPhoneNumber.message}
          </p>
        )}

        <Button
          className="mt-5 rounded-full bg-[#5CB891] hover:bg-[#5CB891]"
          onClick={() => handleContinueNewPhone(watch())}
          type="button"
        >
          Continue
        </Button>
      </>
    ),
    [PhoneUpdateFlowStep.Done]: (
      <div className="mb-1 flex flex-col items-center">
        <img
          src="/assets/images/update-phone-success.png"
          alt="Phone Update Success"
          className="mx-auto my-4 w-32"
        />

        <h3 className="text-center text-sm font-medium">
          Your Phone Number has been updated.
        </h3>

        <div className="mt-5 flex w-full justify-between px-4">
          <p className="text-xs text-[#5B5959]">Date & Time</p>

          <p className="text-xs font-bold text-[#5B5959]">
            {dayjs().format('DD MMM YYYY')} at {dayjs().format('HH:mm A')}
          </p>
        </div>

        <Button
          className="mx-4 mt-7 w-4/5 rounded-full bg-[#5CB891] hover:bg-[#5CB891]"
          onClick={handleCloseDialog}
          type="button"
        >
          Done
        </Button>
      </div>
    )
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2 border-t-1 py-4">
        <div className="flex flex-col gap-1">
          <h3 className="flex items-center gap-2 text-sm font-medium text-gray-900">
            Update Phone Number
            <TooltipProvider>
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 cursor-help text-gray-500" />
                </TooltipTrigger>
                <TooltipContent side="top" className="ml-36 w-56 bg-[#F3F3F3]">
                  <p className="text-xs font-extralight text-[#101B2C]">
                    Update phone number is not available at the moment. If you need
                    assistance, please contact us
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </h3>
          <p className="text-sm text-gray-500">
            Phone number changed? Update it now
          </p>
          <p className="text-sm italic text-gray-500">
            Phone number last updated:{' '}
            {dayjs(lastUpdated).format('MMMM DD, YYYY')}
          </p>
        </div>
        <Button
          type="button"
          className="min-w-24 disabled:bg-gray-700"
          onClick={() => {
            setOpenDialog(true);
          }}
          disabled
        >
          Update
        </Button>
      </div>

      <Dialog open={openDialog} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-[24rem] rounded-3xl">
          {currentStep !== PhoneUpdateFlowStep.Done && (
            <DialogHeader className="mt-10">
              <DialogTitle>{titleMapping[currentStep]}</DialogTitle>
            </DialogHeader>
          )}

          <div className="">
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex flex-col"
            >
              {contentMapping[currentStep]}
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <PhoneVerificationModal
        phoneNumber={verifyPhone}
        onClose={() => {
          setVerifyPhone('');
        }}
        onVerify={(otp) => {
          if (currentStep === PhoneUpdateFlowStep.EnterNewPhone) {
            handleVerifyOtpForNewPhone(otp);
          } else {
            handleVerifyOtpForCurrentPhone(otp);
          }
        }}
        isOpen={verifyPhone !== ''}
        verifyError={
          currentStep === PhoneUpdateFlowStep.EnterNewPhone
            ? confirmChangePhone.error
            : verifyOtpForCurrentPhone.error
        }
        screen={FaceVerifyScreens.UPDATE_PHONE}
      />

      {/* Face Verification Error Dialog */}
      <Dialog
        open={showFaceVerificationError}
        onOpenChange={setShowFaceVerificationError}
      >
        <DialogContent className="max-w-[24rem] rounded-3xl">
          <div className="mb-2 flex flex-col items-center">
            <FailedSVG className="-mt-4 h-40 w-40" />

            <p className="mx-4 mb-4 mt-3 text-center text-sm font-medium text-foreground/90">
              Failed to verify face. Please try again or contact support if the
              issue persists.
            </p>

            <Button
              className="mx-4 mt-4 w-4/5 rounded-full bg-[#5CB891] py-2 text-sm font-medium hover:bg-[#5CB891]"
              onClick={handleCloseDialog}
              type="button"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UpdatePhoneNumber;
